import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import { useAuth } from '../../hooks/useAuth';
import { getAllUsers, updateUserRole } from '../../services/adminService';
import { AppUser, UserRole } from '../../types';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import { useLanguage } from '../../contexts/LanguageContext';

const ROLES: UserRole[] = ['guest', 'user', 'manager', 'admin'];

const roleColor: Record<UserRole, string> = {
  admin: '#DC2626',
  manager: '#D97706',
  user: '#2563EB',
  guest: '#6B7280',
};

export default function AdminUsersScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState<UserRole | null>(null);

  const isAdmin = appUser?.role === 'admin';

  const load = async () => {
    setLoading(true);
    const u = await getAllUsers();
    setUsers(u);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleRoleChange = (user: AppUser, newRole: UserRole) => {
    if (newRole === user.role) return;
    CrossPlatformAlert.alert(
      t('admin.changeRole'),
      t('admin.changeRoleMessage', user.email, newRole),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.confirm'), onPress: async () => {
            try {
              await updateUserRole(user.uid, newRole, user.role);
              load();
            } catch (err: any) {
              CrossPlatformAlert.alert(t('common.error'), err.message);
            }
          }
        }
      ]
    );
  };

  if (!isAdmin) {
    return <View style={styles.center}><Text style={styles.noAccess}>{t('admin.noAccess')}</Text></View>;
  }

  const filteredUsers = filterRole ? users.filter(u => u.role === filterRole) : users;

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('admin.userManagement')}</Text>
          <TouchableOpacity onPress={load} style={styles.refreshBtn}>
            <Text style={styles.refreshText}>↻</Text>
          </TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterRow} contentContainerStyle={styles.filterContent} keyboardShouldPersistTaps="handled">
          <TouchableOpacity
            style={[styles.filterBtn, filterRole === null && styles.filterBtnActive]}
            onPress={() => setFilterRole(null)}
          >
            <Text style={[styles.filterBtnText, filterRole === null && styles.filterBtnTextActive]}>
              {t('admin.filterAll')}
            </Text>
          </TouchableOpacity>
          {ROLES.map(role => (
            <TouchableOpacity
              key={role}
              style={[styles.filterBtn, filterRole === role && { backgroundColor: roleColor[role], borderColor: roleColor[role] }]}
              onPress={() => setFilterRole(filterRole === role ? null : role)}
            >
              <Text style={[styles.filterBtnText, filterRole === role && styles.filterBtnTextActive]}>
                {role.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        {loading ? (
          <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
        ) : (
          <FlatList
            style={styles.list}
            data={filteredUsers}
            keyExtractor={item => item.uid}
            renderItem={({ item }) => (
              <View style={styles.card}>
                <View style={styles.userInfo}>
                  <Text style={styles.userName}>{item.firstname} {item.lastname}</Text>
                  <Text style={styles.userEmail}>{item.email}</Text>
                  <View style={[styles.roleBadge, { backgroundColor: roleColor[item.role] }]}>
                    <Text style={styles.roleText}>{item.role.toUpperCase()}</Text>
                  </View>
                  <Text style={styles.userMeta}>{t('admin.createdAt')}: {item.createdAt.toLocaleString()}</Text>
                  <Text style={styles.userMeta}>{t('admin.lastLoginAt')}: {item.lastLoginAt ? item.lastLoginAt.toLocaleString() : t('admin.never')}</Text>
                </View>
                {item.uid !== appUser?.uid && (
                  <View style={styles.roleButtons}>
                    {ROLES.map(role => (
                      <TouchableOpacity
                        key={role}
                        style={[styles.roleBtn, item.role === role && { backgroundColor: roleColor[role] }]}
                        onPress={() => handleRoleChange(item, role)}
                      >
                        <Text style={[styles.roleBtnText, item.role === role && { color: '#FFF' }]}>
                          {role}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            )}
            ListEmptyComponent={<Text style={styles.empty}>{t('admin.noUsers')}</Text>}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  refreshBtn: { padding: 8 },
  refreshText: { fontSize: 20, color: '#2563EB' },
  filterRow: { borderBottomWidth: 1, borderBottomColor: '#E2E8F0', backgroundColor: '#F8FAFC', zIndex: 1, maxHeight: 40 },
  filterContent: { flexDirection: 'row', paddingHorizontal: 12, paddingVertical: 6, gap: 6 },
  filterBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 16, borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#F8FAFC' },
  filterBtnActive: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  filterBtnText: { fontSize: 12, fontWeight: '600', color: '#475569' },
  filterBtnTextActive: { color: '#FFF' },
  list: { flex: 1 },
  card: { backgroundColor: '#FFF', margin: 8, borderRadius: 12, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 1 },
  userInfo: { marginBottom: 12 },
  userName: { fontSize: 16, fontWeight: '600', color: '#1E293B' },
  userEmail: { fontSize: 13, color: '#64748B', marginTop: 2 },
  userMeta: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  roleBadge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12, alignSelf: 'flex-start', marginTop: 6 },
  roleText: { color: '#FFF', fontWeight: '700', fontSize: 11 },
  roleButtons: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignSelf: 'flex-start' },
  roleBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#F8FAFC' },
  roleBtnText: { fontSize: 12, fontWeight: '600', color: '#475569' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 40, fontSize: 16 },
});
